import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MessageCircle, UploadCloud, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from "sonner";

const ContactDevButton = () => {
   const [isOpen, setIsOpen] = useState(false);
   const [formData, setFormData] = useState({
      email: '',
      whatHappened: '',
      expectedBehavior: '',
      stepsToReproduce: '',
      additionalContext: ''
   });
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [files, setFiles] = useState([]);

   const handleSubmit = async () => {
      if (!formData.email) {
         toast.error("Please provide your email address");
         return;
      }

      if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
         toast.error("Please provide a valid email address");
         return;
      }

      setIsSubmitting(true);
      try {
         const emailBody = `
            Bug Report Details:
            From: ${formData.email}

            1. What happened?
            ${formData.whatHappened}

            2. Expected behavior:
            ${formData.expectedBehavior}

            3. Steps to reproduce:
            ${formData.stepsToReproduce}

            4. Additional context:
            ${formData.additionalContext}

            Number of attachments: ${files.length}
            ${files.map(f => `- ${f.name} (${(f.size / 1024).toFixed(1)} KB)`).join('\n')}
         `;

         window.open(`mailto:markgilbertreginio96204@gmail.com?subject=Bug Report - PermitTree&body=${encodeURIComponent(emailBody)}`, '_blank');

         setIsOpen(false);
         toast.success("Bug report submitted successfully");
         setFormData({
            email: '',
            whatHappened: '',
            expectedBehavior: '',
            stepsToReproduce: '',
            additionalContext: ''
         });
         setFiles([]);
      } catch (error) {
         toast.error("Failed to submit bug report");
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleFileDrop = (e) => {
      e.preventDefault();
      const droppedFiles = Array.from(e.dataTransfer?.files || []);
      handleFiles(droppedFiles);
   };

   const handleFileSelect = (e) => {
      const selectedFiles = Array.from(e.target.files || []);
      handleFiles(selectedFiles);
   };

   const handleFiles = (newFiles) => {
      const MAX_SIZE = 10 * 1024 * 1024;
      const validFiles = newFiles.filter(file => file.size <= MAX_SIZE);

      if (validFiles.length !== newFiles.length) {
         toast.error("Some files were too large. Maximum size is 10MB per file.");
      }

      setFiles(prev => [...prev, ...validFiles]);
   };

   const removeFile = (index) => {
      setFiles(prev => prev.filter((_, i) => i !== index));
   };

   return (
      <>
         <div className="fixed bottom-4 right-4 z-50 pr-6 pb-2">
            <TooltipProvider>
               <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                     <Button
                        onClick={() => setIsOpen(true)}
                        size="icon"
                        className="h-10 w-10 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg"
                     >
                        <MessageCircle className="h-5 w-5" />
                     </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-gray-900 text-white">
                     <p>Report a bug</p>
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         </div>

         <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="max-w-2xl">
               <DialogHeader>
                  <DialogTitle>Report a Bug</DialogTitle>
                  <DialogDescription>
                     Help improve the system by reporting any issues you encounter.
                     <div className="mt-2 p-2 bg-muted rounded-md border">
                        <span className="font-medium text-foreground">Developer contact: </span>
                        <span className="font-bold text-green-600 dark:text-green-500">
                           markgilbertreginio96204@gmail.com
                        </span>
                     </div>
                  </DialogDescription>
               </DialogHeader>

               <div className="space-y-6 py-4">
                  <div className="space-y-2">
                     <Label>Your Email Address <span className="text-red-500">*</span></Label>
                     <input
                        type="email"
                        required
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                           ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium
                           placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2
                           focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                     />
                     <p className="text-xs text-muted-foreground">
                        We'll use this to follow up on your report if needed
                     </p>
                  </div>

                  <div className="space-y-2">
                     <Label>What happened?</Label>
                     <Textarea
                        placeholder="Describe the issue you encountered..."
                        value={formData.whatHappened}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatHappened: e.target.value }))}
                     />
                  </div>

                  <div className="space-y-2">
                     <Label>What did you expect to happen?</Label>
                     <Textarea
                        placeholder="Describe what you expected..."
                        value={formData.expectedBehavior}
                        onChange={(e) => setFormData(prev => ({ ...prev, expectedBehavior: e.target.value }))}
                     />
                  </div>

                  <div className="space-y-2">
                     <Label>Steps to reproduce</Label>
                     <Textarea
                        placeholder="List the steps to reproduce this issue..."
                        value={formData.stepsToReproduce}
                        onChange={(e) => setFormData(prev => ({ ...prev, stepsToReproduce: e.target.value }))}
                     />
                  </div>

                  <div className="space-y-2">
                     <Label>Additional context</Label>
                     <Textarea
                        placeholder="Add any other context about the problem..."
                        value={formData.additionalContext}
                        onChange={(e) => setFormData(prev => ({ ...prev, additionalContext: e.target.value }))}
                        className="mb-2"
                     />

                     <div
                        onDrop={handleFileDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-accent/50 transition-colors"
                     >
                        <input
                           type="file"
                           id="file-upload"
                           className="hidden"
                           multiple
                           onChange={handleFileSelect}
                           accept="image/*,.pdf,.doc,.docx"
                        />
                        <label
                           htmlFor="file-upload"
                           className="cursor-pointer flex flex-col items-center gap-2"
                        >
                           <UploadCloud className="h-8 w-8 text-muted-foreground" />
                           <p className="text-sm text-muted-foreground">
                              Drag & drop files here or click to upload
                           </p>
                           <p className="text-xs text-muted-foreground">
                              Supported: Images, PDF, DOC (Max 10MB per file)
                           </p>
                        </label>
                     </div>

                     {files.length > 0 && (
                        <div className="mt-4 space-y-2">
                           {files.map((file, index) => (
                              <div
                                 key={index}
                                 className="flex items-center justify-between p-2 bg-muted rounded-md"
                              >
                                 <div className="flex items-center gap-2 text-sm">
                                    <span className="truncate max-w-[200px]">{file.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                       ({(file.size / 1024).toFixed(1)} KB)
                                    </span>
                                 </div>
                                 <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeFile(index)}
                                    className="text-red-500 hover:text-red-700"
                                 >
                                    <X className="h-4 w-4" />
                                 </Button>
                              </div>
                           ))}
                        </div>
                     )}
                  </div>
               </div>

               <DialogFooter>
                  <Button variant="outline" onClick={() => setIsOpen(false)}>
                     Cancel
                  </Button>
                  <Button
                     onClick={handleSubmit}
                     disabled={isSubmitting}
                     className="bg-green-600 hover:bg-green-700 text-white"
                  >
                     {isSubmitting ? 'Submitting...' : 'Submit Report'}
                  </Button>
               </DialogFooter>
            </DialogContent>
         </Dialog>
      </>
   );
};

export default ContactDevButton;